import { CardGray } from "@/components/atoms/CardGray"
import { LoadableImage } from "@/components/atoms/LoadableImage"
import { LoadingSpinner } from "@/components/molecules/LoadingSpinner"
import { useNodeGetAll } from "@/lib/api/node"
import { useScanGetLast20 } from "@/lib/api/scan"
import { useScannerGetAll } from "@/lib/api/scanner"
import { useTimelapseGetAll } from "@/lib/api/timelapse"
import { Node as NodeType } from "@/lib/types/node"
import { Scan as ScanType } from "@/lib/types/scan"
import { Scanner as ScannerType } from "@/lib/types/scanner"
import { TimelapseStatus, Timelapse as TimelapseType } from "@/lib/types/timelapse"
import { cn, formatDate, scrollTo } from "@/lib/utils"
import { Badge, Divider, ScrollArea } from "@mantine/core"
import { useNavigate } from "@tanstack/react-router"
import { formatDistanceToNow } from "date-fns"
import { ComponentProps } from "react"
import { FaLink, FaRaspberryPi } from "react-icons/fa6"
import { IoMdQrScanner } from "react-icons/io"

export const Home = () => {
  return (
    <div className="grid md:grid-cols-2 gap-4 md:gap-8">
      <Nodes />
      <Scanners />
      <Timelapses className="md:col-span-2" />
      <Scans className="md:col-span-2" />
    </div>
  )
}

const Nodes = ({ className, ...props }: ComponentProps<"div">) => {
  const { data: nodes, isLoading } = useNodeGetAll()

  const navigate = useNavigate()

  if (isLoading) {
    return (
      <CardGray>
        <LoadingSpinner />
      </CardGray>
    )
  }

  const handleClick = () => {
    scrollTo(navigate, { to: "/nodes" })
  }

  return (
    <CardGray className={cn("flex flex-col gap-4", className)} {...props}>
      <div onClick={handleClick} className="flex items-center gap-2 cursor-pointer w-fit">
        <h2 className="font-bold text-xl">Nodes</h2>
        <FaLink />
      </div>
      <ScrollArea mah={400}>
        <CardGray className="border-gray-100 flex flex-col gap-2">
          {nodes?.map((n, idx) => (
            <>
              <Node key={n.id} node={n} />
              <Divider className={idx === nodes.length - 1 ? "hidden" : ""} />
            </>
          ))}
        </CardGray>
      </ScrollArea>
    </CardGray>
  )
}

const Node = ({ node }: { node: NodeType }) => {
  const navigate = useNavigate()

  const handleClick = () => {
    scrollTo(navigate, { to: "/nodes" }, `node-${node.id}`)
  }

  return (
    <div onClick={handleClick} className="flex flex-col gap-0 transition-transform duration-300 hover:scale-102 cursor-pointer">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FaRaspberryPi />
          <span className="font-bold">{node.name}</span>
        </div>
      </div>
      <p className="text-sm text-muted">{`Scanners: ${node.scanners.length}`}</p>
    </div>
  )
}

const Scanners = ({ className, ...props }: ComponentProps<"div">) => {
  const { data: scanners, isLoading } = useScannerGetAll()

  const navigate = useNavigate()

  if (isLoading) {
    return (
      <CardGray>
        <LoadingSpinner />
      </CardGray>
    )
  }

  const handleClick = () => {
    scrollTo(navigate, { to: "/scanners" })
  }

  return (
    <CardGray className={cn("flex flex-col gap-4", className)} {...props}>
      <div onClick={handleClick} className="flex items-center gap-2 cursor-pointer w-fit">
        <h2 className="font-bold text-xl">Scanners</h2>
        <FaLink />
      </div>
      <ScrollArea mah={400}>
        <CardGray className="border-gray-100 flex flex-col gap-2">
          {scanners?.map((s, idx) => (
            <>
              <Scanner key={s.id} scanner={s} />
              <Divider className={idx === scanners.length - 1 ? "hidden" : ""} />
            </>
          ))}
        </CardGray>
      </ScrollArea>
    </CardGray>
  )
}

const Scanner = ({ scanner }: { scanner: ScannerType }) => {
  const navigate = useNavigate()

  const handleClick = () => {
    scrollTo(navigate, { to: "/scanners" }, `scanner-${scanner.id}`)
  }

  return (
    <div onClick={handleClick} className="flex flex-col gap-0 transition-transform duration-300 hover:scale-102 cursor-pointer">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <IoMdQrScanner />
          <span className="font-bold">{scanner.name}</span>
        </div>
      </div>
      <p className="text-sm text-muted">{`Areas: ${scanner.areas.length}`}</p>
    </div>
  )
}

const Timelapses = ({ className, ...props }: ComponentProps<"div">) => {
  const { data: timelapsesStarted, isLoading: isLoadingStarted } = useTimelapseGetAll({ status: TimelapseStatus.STARTED })
  const { data: timelapsesPaused, isLoading: isLoadingPaused } = useTimelapseGetAll({ status: TimelapseStatus.PAUSED })

  const navigate = useNavigate()

  if (isLoadingStarted || isLoadingPaused) {
    return (
      <CardGray>
        <LoadingSpinner />
      </CardGray>
    )
  }

  const handleClick = () => {
    scrollTo(navigate, { to: "/scanners" })
  }

  return (
    <CardGray className={cn("flex flex-col gap-4 overflow-x-auto", className)} {...props}>
      <div onClick={handleClick} className="flex items-center gap-2 cursor-pointer w-fit">
        <h2 className="font-bold text-xl">Timelapses</h2>
        <FaLink />
      </div>
      <div className="flex items-center gap-4">
        <Badge variant="light" color="green">{`Running: ${timelapsesStarted.length}`}</Badge>
        <Badge variant="light" color="red">{`Paused: ${timelapsesPaused.length}`}</Badge>
      </div>
      <CardGray className="border-gray-100 flex gap-2 overflow-x-auto">
        {timelapsesStarted?.map(t => <Timelapse key={t.id} timelapse={t} />)}
        {timelapsesPaused?.map(t => <Timelapse key={t.id} timelapse={t} />)}
      </CardGray>
    </CardGray>
  )
}

const Timelapse = ({ timelapse }: { timelapse: TimelapseType }) => {
  const navigate = useNavigate()

  const handleClick = () => {
    scrollTo(navigate, { to: "/timelapses/$id", params: { id: timelapse.id.toString() } })
  }

  return (
    <CardGray onClick={handleClick} className="flex-none flex flex-col gap-2 p-0 w-36 md:w-72 overflow-hidden border-gray-100 transition-transform duration-300 hover:scale-102 cursor-pointer">
      <div className="aspect-square w-full">
        {timelapse.coverId ? (
          <LoadableImage src={`/api/scan/${timelapse.coverId}`} />
        ) : (
          <div className="w-full h-full text-center">
            <p className="mt-12">No cover</p>
          </div>
        )}
      </div>
      <div className="flex flex-col p-6 gap-2">
        <span className="font-bold">{timelapse.name}</span>
        {timelapse.lastScan && <span className="text-sm text-muted">{`Last scan: ${formatDistanceToNow(timelapse.lastScan)} ago`}</span>}
      </div>
    </CardGray>
  )
}

const Scans = ({ className, ...props }: ComponentProps<"div">) => {
  const { data: scans, isLoading } = useScanGetLast20()

  if (isLoading) {
    return (
      <CardGray>
        <LoadingSpinner />
      </CardGray>
    )
  }

  return (
    <CardGray className={cn("flex flex-col gap-4 overflow-x-auto", className)} {...props}>
      <h2 className="font-bold text-xl">Scans</h2>
      <CardGray className="border-gray-100 flex gap-2 overflow-x-auto">
        {scans?.map(s => <Scan key={s.id} scan={s} />)}
      </CardGray>
    </CardGray>
  )
}

const Scan = ({ scan }: { scan: ScanType }) => {
  return (
    <CardGray className="flex-none flex flex-col gap-2 p-0 w-36 md:w-72 overflow-hidden border-gray-100">
      <div className="aspect-square w-full">
        <LoadableImage src={`/api/scan/${scan.id}`} />
      </div>
      <div className="p-6">
        <span className="text-sm text-muted">{`Created at: ${formatDate(scan.createdAt)}`}</span>
      </div>
    </CardGray>
  )
}
